import { useRef, useState } from 'react'
import { router, useForm, usePage } from '@inertiajs/react'
import { Building2, Trash2, User, UserRound } from 'lucide-react'
import CreatorShell from '@/components/explore/CreatorShell'
import { Field, TextArea, TextInput } from '@/components/explore/CreatorFields'
import { PUBLIC_CONTAINER } from '@/components/public/publicShell'
import { cn } from '@/lib/utils'
import type { CityOption } from '@/types/publicSchool'

const BIO_MAX = 1200
const AVATAR_MAX_MB = 10
const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp']

interface Creator {
  kind: 'pessoa' | 'empresa'
  first_name: string
  last_name: string
  display_name: string
  headline: string
  bio: string
  city: string
  public_name: string
  avatar_url: string | null
}

interface Props {
  cities: CityOption[]
  creator: Creator
  kinds: { key: string; label: string }[]
}

/**
 * "Fale sobre você" — o perfil da CONTA do criador.
 *
 * Não é da página: quem tem três páginas se apresenta uma vez só, e é a mesma
 * pessoa por trás das três. O que estiver aqui vira o bloco "quem vai te
 * atender" de todas elas, e entra no prompt da IA que escreve os textos.
 *
 * Tudo é opcional. Quem só quer publicar uma página não deveria ser obrigado a
 * escrever uma biografia para conseguir.
 */
export default function Perfil({ cities, creator, kinds }: Props) {
  const form = useForm({
    kind: creator.kind,
    display_name: creator.display_name,
    headline: creator.headline,
    bio: creator.bio,
    city: creator.city,
  })
  const { data, setData, errors, processing } = form

  const [avatarError, setAvatarError] = useState<string | null>(null)
  const [enviandoFoto, setEnviandoFoto] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  // A foto sobe por `router.patch` e não pelo formulário, então o erro dela
  // chega como erro da PÁGINA e não em `form.errors` — que só conhece os campos
  // declarados no useForm.
  const erroDoServidorNaFoto = usePage<{
    errors?: Record<string, string>
  }>().props.errors?.avatar

  const empresa = data.kind === 'empresa'

  // Mesma checagem do servidor, antes de gastar o upload.
  const enviarFoto = (files: FileList | null) => {
    const foto = files?.[0]
    if (!foto) return

    if (!AVATAR_TYPES.includes(foto.type)) {
      setAvatarError('Envie uma imagem JPG, PNG ou WebP.')
      return
    }
    if (foto.size > AVATAR_MAX_MB * 1024 * 1024) {
      setAvatarError(`Imagem muito grande (máximo ${AVATAR_MAX_MB}MB).`)
      return
    }

    setAvatarError(null)
    setEnviandoFoto(true)
    router.patch(
      '/perfil',
      { avatar: foto },
      {
        forceFormData: true,
        preserveScroll: true,
        onFinish: () => {
          setEnviandoFoto(false)
          if (fileRef.current) fileRef.current.value = ''
        },
      },
    )
  }

  return (
    <CreatorShell title="Meu perfil — BeWave Explore" cities={cities}>
      <div className={cn(PUBLIC_CONTAINER, 'flex-1 py-10')}>
        <div className="mx-auto max-w-2xl">
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
            Fale sobre você
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Isto aparece em todas as suas páginas, no bloco de quem está por
            trás do negócio — e é o que a nossa IA usa para escrever com a sua
            voz. Tudo opcional.
          </p>

          <div className="mt-8 space-y-6 rounded-2xl border border-gray-200 bg-white p-6">
            {/* Pessoa ou empresa: muda o título da seção na página pública e a
                voz do texto que a IA escreve. */}
            <div>
              <p className="text-sm font-medium text-gray-700">
                Você é pessoa ou empresa?
              </p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {kinds.map((kind) => {
                  const ativo = data.kind === kind.key
                  const Icone = kind.key === 'empresa' ? Building2 : User
                  return (
                    <button
                      key={kind.key}
                      type="button"
                      aria-pressed={ativo}
                      onClick={() =>
                        setData('kind', kind.key as Creator['kind'])
                      }
                      className={cn(
                        'flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-semibold transition-all duration-200',
                        ativo
                          ? 'border-primary bg-primary/5 text-primary ring-1 ring-primary'
                          : 'border-gray-200 text-gray-700 hover:border-primary/40',
                      )}
                    >
                      <Icone className="size-4" aria-hidden="true" />
                      {kind.label}
                    </button>
                  )
                })}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700">Sua foto</p>
              <div className="mt-2 flex items-center gap-4">
                {creator.avatar_url ? (
                  <img
                    src={creator.avatar_url}
                    alt={creator.public_name}
                    className="size-20 rounded-full object-cover ring-1 ring-gray-100"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="flex size-20 items-center justify-center rounded-full bg-gray-100 text-gray-300"
                  >
                    <UserRound className="size-8" />
                  </span>
                )}

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={enviandoFoto}
                    className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
                  >
                    {enviandoFoto
                      ? 'Enviando…'
                      : creator.avatar_url
                        ? 'Trocar foto'
                        : 'Enviar foto'}
                  </button>
                  {creator.avatar_url && (
                    <button
                      type="button"
                      onClick={() =>
                        router.delete('/perfil/foto', { preserveScroll: true })
                      }
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                      Remover
                    </button>
                  )}
                </div>

                <input
                  ref={fileRef}
                  type="file"
                  accept={AVATAR_TYPES.join(',')}
                  onChange={(e) => enviarFoto(e.target.files)}
                  className="hidden"
                />
              </div>
              {(avatarError || erroDoServidorNaFoto) && (
                <p className="mt-1.5 text-xs text-red-600">
                  {avatarError ?? erroDoServidorNaFoto}
                </p>
              )}
            </div>

            <Field
              label={empresa ? 'Nome da empresa' : 'Como quer ser chamado'}
              hint={`Em branco, usamos "${creator.first_name} ${creator.last_name}".`}
              error={errors.display_name}
            >
              <TextInput
                value={data.display_name}
                onChange={(v) => setData('display_name', v)}
                placeholder={
                  empresa
                    ? 'Digite o nome da empresa'
                    : 'Digite como quer aparecer'
                }
                invalid={Boolean(errors.display_name)}
              />
            </Field>

            <Field
              label="Uma linha sobre você"
              hint="A frase que aparece embaixo do seu nome."
              error={errors.headline}
            >
              <TextInput
                value={data.headline}
                onChange={(v) => setData('headline', v)}
                placeholder={
                  empresa
                    ? 'Digite o que a empresa faz'
                    : 'Digite o que você faz'
                }
                invalid={Boolean(errors.headline)}
              />
            </Field>

            <Field label="Cidade" error={errors.city}>
              <TextInput
                value={data.city}
                onChange={(v) => setData('city', v)}
                placeholder="Digite sua cidade"
              />
            </Field>

            <Field
              label={empresa ? 'Sobre a empresa' : 'Sua história'}
              hint={`${data.bio.length}/${BIO_MAX} caracteres`}
              error={errors.bio}
            >
              <TextArea
                value={data.bio}
                onChange={(v) => setData('bio', v.slice(0, BIO_MAX))}
                rows={7}
                placeholder={
                  empresa
                    ? 'Conte como a empresa começou e o que ela faz melhor'
                    : 'Conte como você começou, o que te move e o que você faz melhor'
                }
              />
            </Field>

            <div className="flex justify-end border-t pt-5">
              <button
                type="button"
                onClick={() => form.patch('/perfil', { preserveScroll: true })}
                disabled={processing}
                className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {processing ? 'Salvando…' : 'Salvar perfil'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </CreatorShell>
  )
}
